import { useCallback, useEffect, useState } from 'react';
import { apiBase } from '../../lib/api';
import { tokens } from '../../theme';
import { Input } from '../../components/Input';
import { Button } from '../../components/Button';
import { PublicNav } from '../../components/nav/PublicNav';
import { Turnstile } from '../../components/Turnstile';

const API_BASE = apiBase();

type SendState = 'idle' | 'sending' | 'sent' | 'error' | 'rate_limited';

type SendStateWithCaptcha = SendState | 'captcha_failed';

export function LoginRequest() {
  const [email, setEmail] = useState('');
  const [state, setState] = useState<SendStateWithCaptcha>('idle');
  const [debugLink, setDebugLink] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  useEffect(() => {
    document.body.classList.add('studio-blue');
    return () => document.body.classList.remove('studio-blue');
  }, []);

  const onVerify = useCallback((token: string) => setTurnstileToken(token), []);
  const onExpire = useCallback(() => setTurnstileToken(null), []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setState('sending');
    setDebugLink(null);
    try {
      const r = await fetch(`${API_BASE}/auth/magic-link/request`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, turnstileToken }),
      });
      if (r.status === 403) {
        setState('captcha_failed');
        return;
      }
      if (r.status === 429) {
        setState('rate_limited');
        return;
      }
      if (!r.ok) {
        setState('error');
        return;
      }
      // Codex r2: server no longer returns debug_link in JSON to avoid
      // token disclosure if Resend is misconfigured. The link only lives
      // in private worker logs now.
      await r.json().catch(() => ({}));
      setState('sent');
    } catch {
      setState('error');
    }
  }

  return (
    <>
      <PublicNav />
      <main
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
          background: tokens.color.paper[100],
        }}
      >
        <div
          style={{
            width: '100%',
            maxWidth: 440,
            padding: 48,
            background: tokens.color.paper['00'],
            borderRadius: 12,
            border: `1px solid ${tokens.color.line[100]}`,
          }}
        >
          <h1
            style={{
              fontFamily: tokens.font.display,
              fontSize: 32,
              fontWeight: 600,
              letterSpacing: '-0.025em',
              color: tokens.color.ink[900],
            }}
          >
            Sign in
          </h1>
          <p style={{ marginTop: 12, color: tokens.color.ink[500] }}>
            We'll email you a link. No password.
          </p>
          <form
            onSubmit={submit}
            style={{ marginTop: 32, display: 'flex', flexDirection: 'column', gap: 16 }}
          >
            <Input
              label="Work email"
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={state === 'sending' || state === 'sent'}
              autoComplete="email"
            />
            <Turnstile onVerify={onVerify} onExpire={onExpire} />
            <Button
              type="submit"
              size="lg"
              loading={state === 'sending'}
              disabled={state === 'sent' || !turnstileToken}
            >
              {state === 'sent'
                ? 'Sent. Check your inbox.'
                : !turnstileToken
                  ? 'Complete the check above'
                  : 'Email me a link'}
            </Button>
            {state === 'rate_limited' && (
              <p style={{ color: tokens.color.accent.rust, fontSize: 13 }}>
                Too many requests. Try again in an hour.
              </p>
            )}
            {state === 'captcha_failed' && (
              <p style={{ color: tokens.color.accent.rust, fontSize: 13 }}>
                Please complete the verification and try again.
              </p>
            )}
            {state === 'error' && (
              <p style={{ color: tokens.color.accent.rust, fontSize: 13 }}>
                Something went wrong. Try again.
              </p>
            )}
          </form>
          {debugLink && (
            <div
              style={{
                marginTop: 20,
                padding: 16,
                background: tokens.color.paper[100],
                border: `1px dashed ${tokens.color.line[100]}`,
                borderRadius: 8,
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  letterSpacing: '0.25em',
                  textTransform: 'uppercase',
                  color: tokens.color.ink[500],
                  marginBottom: 8,
                }}
              >
                Dev fallback (email not yet wired)
              </div>
              <a
                href={debugLink}
                style={{
                  fontFamily: tokens.font.mono,
                  fontSize: 12,
                  color: tokens.color.ink[700],
                  wordBreak: 'break-all',
                  textDecoration: 'underline',
                }}
              >
                Click here to sign in
              </a>
            </div>
          )}
        </div>
      </main>
    </>
  );
}
